// hooks/useFunnelTracking.js
// Hook para integrar tracking de funnel en el onboarding

import { useEffect, useRef, useCallback } from "react"
import {
  obtenerSesionId,
  limpiarSesion,
  registrarEvento,
  trackInicioOnboarding,
  trackFinExitoso,
  trackAbandono,
  trackCambioPaso,
  trackPasoCompletado,
  trackBusquedaCuit,
  trackResultadoEvaluacion,
  trackAlertaRiesgo,
  TIPOS_EVENTO,
} from "api/funnelApi"

/**
 * Hook para manejar el tracking del funnel de onboarding
 *
 * @param {Object} config - Configuración del tracking
 * @param {string[]} config.nombresPasos - Array con nombres de los pasos
 * @param {string} config.canal - Canal de origen (opcional)
 * @param {string} config.campania - Campaña (opcional)
 */
const useFunnelTracking = (config = {}) => {
  const {
    nombresPasos = [
      "Identificación",
      "Documentación",
      "KYC / AML",
      "Evaluación",
      "Firmantes",
      "Garantías",
      "Aprobación",
    ],
    canal,
    campania,
  } = config

  const sesionIdRef = useRef(null)
  const pasoActualRef = useRef(0)
  const iniciadoRef = useRef(false)
  const cuitRef = useRef(null)

  // Inicializar sesión
  useEffect(() => {
    sesionIdRef.current = obtenerSesionId()
  }, [])

  // Registrar abandono al cerrar/salir
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        iniciadoRef.current &&
        pasoActualRef.current < nombresPasos.length - 1
      ) {
        trackAbandono(
          pasoActualRef.current,
          nombresPasos[pasoActualRef.current],
          "Cerró la ventana",
        )
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [nombresPasos])

  // ============================================
  // Funciones de tracking
  // ============================================

  /**
   * Trackea el inicio del onboarding
   */
  const trackInicio = useCallback(() => {
    if (!iniciadoRef.current) {
      iniciadoRef.current = true
      trackInicioOnboarding(canal, campania)
    }
  }, [canal, campania])

  /**
   * Trackea el cambio de paso
   */
  const trackCambioDeTab = useCallback(
    (pasoAnterior, pasoNuevo) => {
      if (!iniciadoRef.current) {
        trackInicio()
      }

      // Registrar paso completado si avanzamos
      if (pasoNuevo > pasoAnterior) {
        trackPasoCompletado(pasoAnterior, nombresPasos[pasoAnterior])
      }

      // Registrar inicio del nuevo paso
      trackCambioPaso(pasoAnterior, pasoNuevo, nombresPasos[pasoNuevo])

      pasoActualRef.current = pasoNuevo
    },
    [nombresPasos, trackInicio],
  )

  /**
   * Trackea búsqueda de CUIT
   */
  const trackCuit = useCallback((cuit, encontrado) => {
    cuitRef.current = cuit
    trackBusquedaCuit(cuit, encontrado)
  }, [])

  /**
   * Trackea carga de documento
   */
  const trackDocumento = useCallback((tipoDocumento, exitoso) => {
    registrarEvento(
      exitoso
        ? TIPOS_EVENTO.DOCUMENTO_CARGADO
        : TIPOS_EVENTO.DOCUMENTO_RECHAZADO,
      {
        cuit: cuitRef.current,
        paso: pasoActualRef.current,
        datosAdicionales: { tipoDocumento },
      },
    )
  }, [])

  /**
   * Trackea resultado de KYC
   */
  const trackKYC = useCallback(resultado => {
    registrarEvento(TIPOS_EVENTO.KYC_COMPLETADO, {
      cuit: cuitRef.current,
      paso: pasoActualRef.current,
      resultado: resultado.nivelRiesgoGlobal,
    })

    // Si hay alertas, registrarlas
    if (
      resultado.nivelRiesgoGlobal === "ALTO" ||
      resultado.nivelRiesgoGlobal === "CRITICO"
    ) {
      trackAlertaRiesgo("KYC", resultado.nivelRiesgoGlobal, cuitRef.current)
    }
  }, [])

  /**
   * Trackea consulta de Nosis
   */
  const trackNosis = useCallback(resultado => {
    registrarEvento(TIPOS_EVENTO.NOSIS_CONSULTADO, {
      cuit: cuitRef.current,
      paso: pasoActualRef.current,
      resultado: resultado.nivelRiesgo,
      datosAdicionales: { score: resultado.score },
    })

    if (
      resultado.nivelRiesgo === "ALTO" ||
      resultado.nivelRiesgo === "CRITICO"
    ) {
      trackAlertaRiesgo("NOSIS", resultado.nivelRiesgo, cuitRef.current)
    }
  }, [])

  /**
   * Trackea resultado de evaluación
   */
  const trackEvaluacion = useCallback((resultado, calificacion) => {
    trackResultadoEvaluacion(cuitRef.current, resultado, calificacion)
  }, [])

  /**
   * Trackea extracción de estatuto
   */
  const trackEstatuto = useCallback((exitoso, cantidadFirmantes) => {
    registrarEvento(
      exitoso ? TIPOS_EVENTO.ESTATUTO_EXTRAIDO : TIPOS_EVENTO.ERROR_SISTEMA,
      {
        cuit: cuitRef.current,
        paso: pasoActualRef.current,
        datosAdicionales: { cantidadFirmantes },
      },
    )
  }, [])

  /**
   * Trackea validación biométrica
   */
  const trackBiometria = useCallback((estado, documento) => {
    const tipoEvento = {
      EN_PROCESO: TIPOS_EVENTO.BIOMETRIA_INICIADA,
      APROBADA: TIPOS_EVENTO.BIOMETRIA_COMPLETADA,
      RECHAZADA: TIPOS_EVENTO.BIOMETRIA_RECHAZADA,
    }[estado]

    if (tipoEvento) {
      registrarEvento(tipoEvento, {
        cuit: cuitRef.current,
        paso: pasoActualRef.current,
        datosAdicionales: { documento },
      })
    }
  }, [])

  /**
   * Trackea garantía agregada/eliminada
   */
  const trackGarantia = useCallback((accion, tipoGarantia, monto) => {
    registrarEvento(
      accion === "agregar"
        ? TIPOS_EVENTO.GARANTIA_AGREGADA
        : TIPOS_EVENTO.GARANTIA_ELIMINADA,
      {
        cuit: cuitRef.current,
        paso: pasoActualRef.current,
        datosAdicionales: { tipoGarantia, monto },
      },
    )
  }, [])

  /**
   * Trackea decisión de aprobación
   */
  const trackAprobacion = useCallback((nivel, estado, motivo) => {
    const tipoEventoMap = {
      aprobado: TIPOS_EVENTO.APROBACION_OTORGADA,
      rechazado: TIPOS_EVENTO.APROBACION_RECHAZADA,
      condicionado: TIPOS_EVENTO.APROBACION_CONDICIONADA,
    }

    registrarEvento(
      tipoEventoMap[estado] || TIPOS_EVENTO.APROBACION_SOLICITADA,
      {
        cuit: cuitRef.current,
        paso: pasoActualRef.current,
        resultado: estado,
        motivo,
        datosAdicionales: { nivel },
      },
    )
  }, [])

  /**
   * Trackea fin exitoso
   */
  const trackFin = useCallback(() => {
    trackFinExitoso(cuitRef.current)
    limpiarSesion()
  }, [])

  /**
   * Trackea abandono manual
   */
  const trackAbandonoManual = useCallback(
    motivo => {
      trackAbandono(
        pasoActualRef.current,
        nombresPasos[pasoActualRef.current],
        motivo,
      )
    },
    [nombresPasos],
  )

  /**
   * Trackea recuperación de progreso
   */
  const trackRecuperacion = useCallback(accion => {
    registrarEvento(
      accion === "recuperar"
        ? TIPOS_EVENTO.PROGRESO_RECUPERADO
        : TIPOS_EVENTO.PROGRESO_DESCARTADO,
      { cuit: cuitRef.current },
    )
  }, [])

  /**
   * Trackea error del sistema
   */
  const trackError = useCallback((mensaje, contexto) => {
    registrarEvento(TIPOS_EVENTO.ERROR_SISTEMA, {
      cuit: cuitRef.current,
      paso: pasoActualRef.current,
      motivo: mensaje,
      datosAdicionales: contexto,
    })
  }, [])

  return {
    // Funciones principales
    trackInicio,
    trackCambioDeTab,
    trackFin,
    trackAbandonoManual,

    // Funciones específicas
    trackCuit,
    trackDocumento,
    trackKYC,
    trackNosis,
    trackEvaluacion,
    trackEstatuto,
    trackBiometria,
    trackGarantia,
    trackAprobacion,
    trackRecuperacion,
    trackError,

    // Helpers
    setSesionCuit: cuit => {
      cuitRef.current = cuit
    },
    getSesionId: () => sesionIdRef.current,
  }
}

export default useFunnelTracking
